import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Logo } from '../components/Logo';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Hammer, Building2, Briefcase, Users, Check } from 'lucide-react';

export default function Onboarding() {
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [step, setStep] = useState(1);
    const [data, setData] = useState({
        company_type: '',
        sectors: [],
        first_cantiere: { nome: '', cliente: '', indirizzo: '', importo: 0 },
        notifications: { email: true, whatsapp: false, telegram: false },
    });

    const SECTORS = t('onboarding.sectors', { returnObjects: true }) || [];
    const COMPANY_TYPES = [
        { id: 'artigiano', icon: Hammer, label: t('onboarding.s1_artigiano') },
        { id: 'piccola_impresa', icon: Briefcase, label: t('onboarding.s1_piccola_impresa') },
        { id: 'impresa', icon: Building2, label: t('onboarding.s1_impresa') },
        { id: 'studio', icon: Users, label: t('onboarding.s1_studio') },
    ];

    const next = () => setStep((s) => Math.min(s + 1, 5));
    const prev = () => setStep((s) => Math.max(s - 1, 1));

    const finish = async () => {
        try {
            await api.post('/auth/onboarding', data);
            await refreshUser();
            toast.success(t('onboarding.success'));
            navigate('/app/dashboard');
        } catch (e) {
            toast.error(t('onboarding.error'));
        }
    };

    const toggleSector = (s) => {
        setData((d) => ({ ...d, sectors: d.sectors.includes(s) ? d.sectors.filter((x) => x !== s) : [...d.sectors, s] }));
    };

    return (
        <div className="min-h-screen bg-[hsl(0_0%_98%)] flex flex-col">
            <div className="p-6 border-b border-[hsl(var(--border))] bg-white flex items-center justify-between">
                <Logo />
                <LanguageSwitcher />
            </div>
            <div className="flex-1 flex items-center justify-center px-4 py-8">
                <Card className="w-full max-w-2xl p-8">
                    <div className="flex items-center justify-between mb-6">
                        <div className="font-display text-xs tracking-widest text-[hsl(var(--muted-foreground))]">{t('onboarding.step_of', { n: step })}</div>
                        <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <div key={n} className={`w-8 h-1 rounded ${n <= step ? 'bg-[hsl(var(--accent))]' : 'bg-[hsl(var(--muted))]'}`} />
                            ))}
                        </div>
                    </div>

                    {step === 1 && (
                        <div>
                            <h2 className="font-display text-3xl mb-2">{t('onboarding.s1_title')}</h2>
                            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">{t('onboarding.s1_subtitle')}</p>
                            <div className="grid grid-cols-2 gap-3">
                                {COMPANY_TYPES.map((tp) => (
                                    <button key={tp.id} onClick={() => setData({ ...data, company_type: tp.id })} className={`p-4 rounded-lg border-2 text-left transition-colors ${data.company_type === tp.id ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--muted-foreground))]'}`} data-testid={`onboarding-type-${tp.id}`}>
                                        <tp.icon className="w-6 h-6 mb-2 text-[hsl(var(--accent))]" />
                                        <div className="font-semibold">{tp.label}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div>
                            <h2 className="font-display text-3xl mb-2">{t('onboarding.s2_title')}</h2>
                            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">{t('onboarding.s2_subtitle')}</p>
                            <div className="flex flex-wrap gap-2">
                                {SECTORS.map((s) => (
                                    <button key={s} onClick={() => toggleSector(s)} className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${data.sectors.includes(s) ? 'bg-[hsl(var(--accent))] border-[hsl(var(--accent))] text-[hsl(220_13%_10%)]' : 'bg-white border-[hsl(var(--border))] hover:border-[hsl(var(--muted-foreground))]'}`} data-testid={`onboarding-sector-${s.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div>
                            <h2 className="font-display text-3xl mb-2">{t('onboarding.s3_title')}</h2>
                            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">{t('onboarding.s3_subtitle')}</p>
                            <div className="space-y-4">
                                <div><Label>{t('onboarding.cantiere_name')}</Label><Input value={data.first_cantiere.nome} onChange={(e) => setData({...data, first_cantiere: {...data.first_cantiere, nome: e.target.value}})} placeholder={t('onboarding.cantiere_name_ph')} className="mt-1" data-testid="onboarding-cantiere-name" /></div>
                                <div><Label>{t('onboarding.cantiere_cliente')}</Label><Input value={data.first_cantiere.cliente} onChange={(e) => setData({...data, first_cantiere: {...data.first_cantiere, cliente: e.target.value}})} className="mt-1" /></div>
                                <div><Label>{t('onboarding.cantiere_indirizzo')}</Label><Input value={data.first_cantiere.indirizzo} onChange={(e) => setData({...data, first_cantiere: {...data.first_cantiere, indirizzo: e.target.value}})} className="mt-1" /></div>
                                <div><Label>{t('onboarding.cantiere_importo')}</Label><Input type="number" value={data.first_cantiere.importo} onChange={(e) => setData({...data, first_cantiere: {...data.first_cantiere, importo: parseFloat(e.target.value) || 0}})} className="mt-1" /></div>
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div>
                            <h2 className="font-display text-3xl mb-2">{t('onboarding.s4_title')}</h2>
                            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">{t('onboarding.s4_subtitle')}</p>
                            <div className="space-y-3">
                                {[
                                    { key: 'email', label: t('onboarding.notif_email') },
                                    { key: 'whatsapp', label: t('onboarding.notif_wa') },
                                    { key: 'telegram', label: t('onboarding.notif_tg') },
                                ].map((n) => (
                                    <label key={n.key} className="flex items-center gap-3 p-3 rounded-lg border border-[hsl(var(--border))] cursor-pointer hover:bg-[hsl(var(--muted))]">
                                        <Checkbox checked={data.notifications[n.key]} onCheckedChange={(v) => setData({...data, notifications: {...data.notifications, [n.key]: v}})} />
                                        <span className="font-medium">{n.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 5 && (
                        <div className="text-center py-6">
                            <div className="w-16 h-16 mx-auto bg-[hsl(var(--accent))] rounded-full flex items-center justify-center mb-4">
                                <Check className="w-8 h-8 text-[hsl(220_13%_10%)]" />
                            </div>
                            <h2 className="font-display text-4xl mb-2">{t('onboarding.all_ready')}</h2>
                            <p className="text-[hsl(var(--muted-foreground))] mb-6">{t('onboarding.welcome_named', { name: user?.full_name?.split(' ')[0] || '' })}</p>
                            <Button onClick={finish} className="bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-11 px-8" data-testid="onboarding-finish-button">
                                {t('onboarding.finish_btn')}
                            </Button>
                        </div>
                    )}

                    {step < 5 && (
                        <div className="flex justify-between mt-8 pt-6 border-t border-[hsl(var(--border))]">
                            <Button variant="outline" onClick={prev} disabled={step === 1}>{t('onboarding.back')}</Button>
                            <Button onClick={next} disabled={step === 1 && !data.company_type} className="bg-[hsl(220_13%_10%)] text-white hover:bg-[hsl(220_13%_20%)]" data-testid="onboarding-next-button">
                                {t('onboarding.next')}
                            </Button>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
}
